import { css } from 'styled-components';

import { gstarColors } from './BaseStyledColorScheme';
import { BaseStyledStyling } from './BaseStyledStyling';
import { media } from '~/components/shared/core/styles/media';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

/**
 * Stacking ladder for everything inside a .contentBlock, bottom to top.
 *
 * ```
 * 40  control    --contentBlock-control-z-index    vimeo mute button
 * 30  hotspots   --contentBlock-hotspots-z-index   hotspot markers
 * 20  button     --contentBlock-button-z-index     body-link buttons (must beat the hitarea)
 * 10  clickable  --contentBlock-clickable-z-index  full-block hitarea
 *  -  body       .contentBlock-body         - implicit, DOM order
 *  -  media      picture / vimeo container  - implicit, DOM order
 * ```
 *
 * Magnitude is free: `isolation: isolate` makes every block its own stacking context, so these
 * only ever compete with each other. Steps of 10 leave room to slot a layer in between without
 * renumbering - which matters because the fallbacks below live in other files and have to move
 * in lockstep.
 *
 * Two rules keep this working:
 *
 * - Every consumer reads `var(--contentBlock-<layer>-z-index, <the same number>)`. The fallback
 *   is not decoration - `VimeoPlayer` and `Hotspots` also render outside a .contentBlock (the
 *   Collection Hero, for one), and the literal is what keeps them layered there. A ladder value
 *   changed here without its fallbacks fails silently: correct inside a block, wrong outside.
 * - Never `calc()` off a variable the component does not declare itself. One missing ancestor
 *   variable makes the whole declaration invalid at computed-value time, and the layer silently
 *   collapses to `auto`.
 *
 * The AI watermark is deliberately not in the ladder: it keeps its own `--ai-icon-z-index`
 * (100, 1101, 30 or auto depending on the surface). Inside an isolated block those large values
 * simply mean "above everything here".
 */
export const contentBlockZIndexLadder = css`
  --contentBlock-clickable-z-index: 10;
  --contentBlock-button-z-index: 20;
  --contentBlock-hotspots-z-index: 30;
  --contentBlock-control-z-index: 40;

  isolation: isolate;
`;

export const BaseStyledElement = {
  /**
   * Base styling for .row Element
   *
   * ```css
   * position: relative;
   * display: grid;
   * grid-template-rows: 1fr;
   * overflow: hidden;
   * ```
   */
  row: css`
    position: relative;
    display: grid;
    grid-template-rows: 1fr;
    overflow: hidden;

    ${BaseStyledStyling.ParallaxSafeOverflow}
  `,
  /**
   * Base styling for .col Element
   *
   * ```css
   * grid-area: 1 / 1;
   * ```
   */
  col: css`
    grid-area: 1 / 1;
  `,
  /**
   * Base styling for .contentBlock Element
   *
   * ```css
   * display: grid;
   * grid-template-rows: 1fr;
   * width: 100%;
   * height: 100%;
   * padding: var(--contentBlock-content-padding, 0);
   * color: var(--contentBlock-text-color, ${gstarColors.slate['900']});
   * > * {
   *   grid-area: 1/1;
   * }
   * ```
   */
  contentBlock: css`
    ${contentBlockZIndexLadder}

    position: relative;
    display: grid;
    grid-template-rows: 1fr;
    width: 100%;
    height: 100%;
    padding: var(--contentBlock-content-padding, 0);
    color: var(--contentBlock-text-color, ${gstarColors.slate['900']});

    &:not(:has(.contentBlock-picture)) {
      background-color: var(--contentBlock-background-color, transparent);
    }

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      --contentBlock-links-display: flex;
    }

    > * {
      grid-area: 1/1;
    }
  `,
  /**
   * Base styling for .contentBlock-picture Element
   *
   * ```css
   * grid-area: 1/1;
   * width: 100%;
   * height: 100%;
   * overflow: hidden;
   * background-color: var(--contentBlock-background-color, ${gstarColors.neutral['50']});
   * ```
   */
  contentBlockPicture: css`
    grid-area: 1/1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: var(--contentBlock-background-color, ${gstarColors.neutral['50']});
  `,
  /**
   * Base styling for .contentBlock-picture-image Element
   *
   * ```css
   * width: 100%;
   * height: 100%;
   * max-width: none;
   * max-height: none;
   * object-fit: cover;
   * object-position: var(--object-position, var(--contentBlock-image-position, 50% 50%));
   * ```
   *
   * `--object-position` carries the authored `data-object-position` and is the name the rest of
   * the codebase uses (Hero, SectionHero, SectionText). `--contentBlock-image-position` is kept
   * as an intermediate fallback for the components that still read it, though nothing sets it.
   */
  contentBlockPictureImage: css`
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    object-fit: cover;
    object-position: var(--object-position, var(--contentBlock-image-position, 50% 50%));
  `,
  /**
   * Base styling for .contentBlock-body Element
   *
   * ```css
   * position: relative;
   * grid-area: 1/1;
   * display: flex;
   * justify-content: var(--contentBlock-justify-content, center);
   * align-items: var(--contentBlock-align-items, center);
   *
   * :where(.DIN)
   * ```
   */
  contentBlockBody: css`
    position: relative;
    grid-area: 1/1;
    display: flex;
    justify-content: var(--contentBlock-justify-content, center);
    align-items: var(--contentBlock-align-items, center);

    :where(.DIN) {
      ${BaseStyledStyling.HeadingStyling}
    }
  `,
  /**
   * Base styling for .contentBlock-bodyAlign Element
   *
   * ```css
   * padding: 20px;
   * text-align: var(--contentBlock-text-align, center);
   * ```
   */
  contentBlockBodyAlign: css`
    padding: var(--contentBlock-body-padding, 24px);
    text-align: var(--contentBlock-text-align, center);
    width: 100%;
  `,
  /**
   * Base styling for .contentBlock-body-picture Element
   *
   * ```css
   *  display: block;
   * ```
   */
  contentBlockBodyPicture: css`
    display: block;
  `,
  /**
   * Base styling for .contentBlock-body-picture-image Element
   *
   * ```css
   *  width: 100%;
   * ```
   */
  contentBlockBodyPictureImage: css`
    width: 100%;
  `,
  /**
   * Base styling for .contentBlock-body-links Element
   *
   * ```css
   *  display: var(--contentBlock-links-display, inline grid);
   *  grid-auto-flow: column;
   *  grid-auto-columns: 1fr;
   *  margin-block-start: var(--contentBlock-button-margin, ${theme.spacing.x.lg});
   *  gap: var(--contentBlock-button-gap, ${theme.spacing.x.xs});
   *  justify-content: var(--contentBlock-justify-content, center);
   *  flex-wrap: wrap;
   *
   *  &:first-child {
   *    margin-block-start: 0;
   *  }
   * ```
   */
  contentBlockBodyLinks: css`
    ${({ theme }) => css`
      display: var(--contentBlock-links-display, flex);
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      margin-block-start: var(--contentBlock-button-margin, ${theme.spacing.x.lg});
      gap: var(--contentBlock-button-gap, ${theme.spacing.x.xs});
      justify-content: var(--contentBlock-justify-content, center);
      flex-wrap: wrap;

      &:first-child {
        margin-block-start: 0;
      }
    `}
  `,
  /**
   * Base styling for .contentBlock-body-link Element
   *
   * ```css
   * display: inline-flex;
   * align-items: center;
   * justify-content: center;
   * min-height: 33px;
   * min-width: 120px;
   * z-index: 100;
   *
   * padding-block: ${theme.spacing.x['2xs']};
   * padding-inline: ${theme.spacing.x.sm};
   * border-radius: var(--contentBlock-button-border-radius, 1px);
   *
   * border: 2px solid var(--contentBlock-button-border-color, ${theme.colors.neutral['800']});
   * background-color: var(--contentBlock-button-background-color, ${theme.colors.neutral['800']});
   * color: var(--contentBlock-button-text-color, ${theme.colors.neutral['0']});
   *
   * font-size: 12px;
   * font-weight: ${theme.variables.font.weight.medium};
   * line-height: 1;
   * text-transform: capitalize;
   *
   * @media (hover: hover) {
   *   &:hover,
   *   &:focus {
   *     --contentBlock-button-border-color: var(--contentBlock-button-hover-border-color, ${theme.colors.slate['600']});
   *     --contentBlock-button-background-color: var(--contentBlock-button-hover-background-color, ${theme.colors.slate['600']});
   *     --contentBlock-button-text-color: var(--contentBlock-button-hover-text-color, ${theme.colors.neutral['0']});
   *   }
   * }
   *
   * &:active {
   *   --contentBlock-button-border-color: var(--contentBlock-button-active-border-color, ${theme.colors.neutral['800']});
   *   --contentBlock-button-background-color: var(--contentBlock-button-active-background-color, ${theme.colors.neutral['800']});
   *   --contentBlock-button-text-color: var(--contentBlock-button-active-text-color, ${theme.colors.neutral['0']});
   * }
   *
   * &:focus-visible {
   *   outline-offset: 3px;
   * }
   * ```
   */
  contentBlockBodyLink: css`
    ${({ theme }) => css`
      --min-width: 120px;
      --padding-block: ${theme.spacing.x.xs};
      --padding-inline: ${theme.spacing.x.md};

      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
      min-width: max-content;
      z-index: var(--contentBlock-button-z-index, 20);
      padding-block: var(--padding-block);
      padding-inline: var(--padding-inline);
      border-radius: var(--contentBlock-button-border-radius, 1px);
      border: 2px solid var(--contentBlock-button-border-color, ${theme.colors.slate['900']});
      background-color: var(--contentBlock-button-background-color, transparent);
      color: var(--contentBlock-button-text-color, ${theme.colors.slate['900']});
      font-size: var(--contentBlock-button-font-size, 14px);
      line-height: 1.1;

      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          /* Gstar, Outlet, Employeeshop, Aaf */
          default: css`
            text-transform: lowercase;
            font-weight: ${theme.variables.font.weight.medium};
          `,
          [SiteType.Wahts]: css`
            --padding-block: 9px 7px;

            text-transform: uppercase;
            font-weight: ${theme.variables.font.weight.regular};
          `,
          [SiteType.Aaf]: css`
            --contentBlock-button-font-size: 12px;
            --contentBlock-button-border-radius: 0;
            --padding-block: ${theme.spacing.y.sm};
            --padding-inline: ${theme.spacing.x.md};
            --min-width: 0;

            border-width: 1px;
            line-height: 1;
            letter-spacing: 0.05em;
            text-transform: uppercase;

            /* outline/underline stay regular; solidButtonVariant sets the var to bold */
            font-weight: var(
              --contentBlock-button-font-weight,
              ${theme.variables.font.weight.regular}
            );
          `,
        })}

      :where(.contentBlock-body-link-label) {
        min-width: calc(var(--min-width) - var(--padding-inline) * 2);
        text-align: center;
      }

      @media (hover: hover) {
        &:hover,
        &:focus {
          --contentBlock-button-border-color: var(
            --contentBlock-button-hover-border-color,
            ${theme.colors.neutral['800']}
          );
          --contentBlock-button-background-color: var(
            --contentBlock-button-hover-background-color,
            ${theme.colors.neutral['800']}
          );
          --contentBlock-button-text-color: var(
            --contentBlock-button-hover-text-color,
            ${theme.colors.neutral['0']}
          );
        }
      }

      &:active {
        --contentBlock-button-border-color: var(
          --contentBlock-button-active-border-color,
          ${theme.colors.slate['900']}
        );
        --contentBlock-button-background-color: var(
          --contentBlock-button-active-background-color,
          ${theme.colors.slate['600']}
        );
        --contentBlock-button-text-color: var(
          --contentBlock-button-active-text-color,
          ${theme.colors.neutral['0']}
        );
      }

      &:focus-visible {
        outline-offset: 3px;
      }
    `}
  `,
  /**
   * Base styling for categoryTag Element
   *
   * ```css
   * ```
   */
  categoryTag: css`
    ${({ theme }) => css`
      --button-font-size: var(--categoryTag-font-size, 12px);
      --min-width: 0;

      display: inline-flex;
      align-items: center;
      justify-content: center;
      z-index: 100;
      border-radius: var(--categoryTag-border-radius, 1px);
      border: 1px solid var(--categoryTag-border-color, ${gstarColors.slate['900']});
      background-color: var(--categoryTag-background-color, transparent);
      color: var(--categoryTag-text-color, ${gstarColors.slate['900']});
      padding-block: ${theme.spacing.x['2xs']};
      padding-inline: ${theme.spacing.x.md};
      min-width: max-content;
      min-height: 28px;
      font-size: var(--button-font-size);
      line-height: 1;
      font-weight: ${theme.variables.font.weight.regular};
      text-transform: capitalize;

      &:not(.is-active) {
        &:hover,
        &:focus,
        &:active {
          padding-block-end: calc(${theme.spacing.x['2xs']} - 1px);
          border-block-end-width: 2px;
        }
      }

      @media (min-resolution: 2dppx) {
        border-width: 0.5px;
        min-height: 26px;
        transform: translate3d(0, 0, 0);
        text-rendering: auto;

        &:not(.is-active) {
          &:hover,
          &:focus,
          &:active {
            padding-block-end: calc(${theme.spacing.x['2xs']} - 0.5px);
            border-block-end-width: 1px;
          }
        }
      }

      @media (resolution: 3dppx) {
        border-width: 0.68px;

        &:not(.is-active) {
          &:hover,
          &:focus,
          &:active {
            padding-block-end: calc(${theme.spacing.x['2xs']} - 1.33px);
            border-block-end-width: 2px;
          }
        }
      }

      @media (hover: hover) {
        &:hover,
        &:focus {
          --categoryTag-border-color: var(
            --categoryTag-hover-border-color,
            ${gstarColors.slate['900']}
          );
          --categoryTag-background-color: var(--categoryTag-hover-background-color, #21252911);
          --categoryTag-text-color: var(
            --categoryTag-hover-text-color,
            ${gstarColors.slate['900']}
          );
        }
      }

      &:active {
        --categoryTag-border-color: var(
          --categoryTag-active-border-color,
          ${gstarColors.slate['900']}
        );
        --categoryTag-background-color: var(--categoryTag-active-background-color, #21252911);
        --categoryTag-text-color: var(--categoryTag-active-text-color, ${gstarColors.slate['900']});
      }

      &.is-active {
        --categoryTag-border-color: var(
          --categoryTag-is-active-border-color,
          ${gstarColors.slate['500']}
        );
        --categoryTag-background-color: var(
          --categoryTag-is-active-background-color,
          ${gstarColors.slate['900']}
        );
        --categoryTag-text-color: var(
          --categoryTag-is-active-text-color,
          ${gstarColors.neutral['0']}
        );
      }

      &:focus-visible {
        outline-offset: 3px;
      }
    `}
  `,
};

export const {
  row,
  col,
  contentBlock,
  contentBlockPicture,
  contentBlockPictureImage,
  contentBlockBody,
  contentBlockBodyPicture,
  contentBlockBodyPictureImage,
  contentBlockBodyAlign,
  contentBlockBodyLinks,
  contentBlockBodyLink,
  categoryTag,
} = BaseStyledElement;
